import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  CONTACT_NOTE_MAX, LOCATION_MAX, PRIVATE_ADDRESS_MAX, SALE_DESCRIPTION_MAX, SALE_STATUSES, TITLE_MAX,
  type PlatformSale, type SaleMemberInput, type SaleRole, type SaleStatus, type Seller,
} from '@sellit/shared';
import { platform } from '../lib/api';
import { SALE_STATUS_HELP, SALE_STATUS_LABEL, setAppTitle, slugify, TIMEZONES } from '../lib/format';
import { Icon } from '../lib/Icon';
import { CopyButton, errorText, fieldErrors, SaleStatusChip, useLoad } from './common';

/** New sale (no :saleId) or edit an existing one. */
export function SaleFormPage() {
  const { saleId } = useParams();
  const sellers = useLoad(() => platform.sellers());
  const sale = useLoad(() => (saleId ? platform.sale(saleId) : Promise.resolve(null)), [saleId]);
  useEffect(() => setAppTitle(`${saleId ? 'Edit sale' : 'New sale'}`), [saleId]);

  const error = sellers.error ?? sale.error;
  if (error) return <main className="page narrow pa-page"><p className="error">{error}</p></main>;
  if (!sellers.data || (saleId && !sale.data)) return <main className="page narrow pa-page muted">Loading…</main>;
  return <SaleForm key={sale.data?.id ?? 'new'} sale={sale.data} sellers={sellers.data.sellers} onSaved={sale.setData} />;
}

interface Form {
  title: string;
  slug: string;
  description: string;
  publicLocation: string;
  privateAddress: string;
  contactNote: string;
  endsOn: string;
  timezone: string;
  status: SaleStatus;
}

const toForm = (s: PlatformSale | null): Form => ({
  title: s?.title ?? '',
  slug: s?.slug ?? '',
  description: s?.description ?? '',
  publicLocation: s?.publicLocation ?? '',
  privateAddress: s?.privateAddress ?? '',
  contactNote: s?.contactNote ?? '',
  endsOn: s?.endsOn ?? '',
  timezone: s?.timezone ?? 'America/Chicago',
  status: s?.status ?? 'DRAFT',
});

function SaleForm({ sale, sellers, onSaved }: { sale: PlatformSale | null; sellers: Seller[]; onSaved: (s: PlatformSale) => void }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState<Form>(() => {
    const initial = toForm(sale);
    // From a Start Selling request: start with the sale name they asked for.
    const title = sale ? null : params.get('title')?.trim().slice(0, TITLE_MAX);
    return title ? { ...initial, title, slug: slugify(title) } : initial;
  });
  const [members, setMembers] = useState<SaleMemberInput[]>(() => {
    if (sale) return sale.members.map((m) => ({ userId: m.userId, role: m.role }));
    const preset = params.get('seller');
    return preset && sellers.some((s) => s.id === preset) ? [{ userId: preset, role: 'OWNER' }] : [];
  });
  const slugTouched = useRef(Boolean(sale));
  const [slugCheck, setSlugCheck] = useState<{ slug: string; available: boolean; reason: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const set = (patch: Partial<Form>) => {
    setSaved(false);
    setForm((f) => ({ ...f, ...patch }));
  };
  const origin = window.location.origin;
  const publicUrl = `${origin}/${form.slug}`;

  // Live slug check, debounced.
  useEffect(() => {
    if (!form.slug || form.slug === sale?.slug) {
      setSlugCheck(null);
      return;
    }
    const slug = form.slug;
    const t = setTimeout(() => {
      platform.slugAvailable(slug, sale?.id).then((r) => setSlugCheck({ slug, ...r }), () => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [form.slug, sale?.slug, sale?.id]);

  const slugChangedWhileLive = sale && form.slug !== sale.slug && (sale.status === 'ACTIVE' || sale.status === 'CLOSED');
  const noOwner = !members.some((m) => m.role === 'OWNER');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (slugChangedWhileLive && !window.confirm(`Change the link from /${sale!.slug} to /${form.slug}? The old link will stop working for anyone who has it.`)) return;
    setBusy(true);
    setError(null);
    setFields({});
    const body = {
      ...form,
      privateAddress: form.privateAddress || null,
      contactNote: form.contactNote || null,
      endsOn: form.endsOn || null,
    };
    try {
      if (!sale) {
        const created = await platform.createSale({ ...body, members });
        navigate(`/platform-admin/sales/${created.id}`, { replace: true, state: { created: true } });
        return;
      }
      let updated = await platform.updateSale(sale.id, body);
      const before = JSON.stringify(sale.members.map((m) => [m.userId, m.role]));
      if (JSON.stringify(members.map((m) => [m.userId, m.role])) !== before) {
        updated = await platform.setMembers(sale.id, members);
      }
      onSaved(updated);
      setForm(toForm(updated));
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
      setFields(fieldErrors(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page narrow pa-page">
      <Link className="link small" to="/platform-admin/sales">← Sales</Link>
      <header className="pa-head">
        <h1>{sale ? sale.title : 'New sale'}</h1>
        {sale && <SaleStatusChip status={sale.status} />}
      </header>

      {sale && (
        <>
          <nav className="tool-grid" aria-label="Sale tools">
            <a className="tool" href={`/${sale.slug}`} target="_blank" rel="noreferrer">
              <Icon name="eye" /><span>{sale.status === 'DRAFT' || sale.status === 'ARCHIVED' ? 'Preview' : 'View page'}</span>
            </a>
            <CopyButton className="tool" text={`${origin}/${sale.slug}`} label="Copy link" />
            <Link className="tool" to={`/admin/sales/${sale.id}`}>
              <Icon name="tag" /><span>Items ({sale.itemCount})</span>
            </Link>
            <Link className="tool" to={`/admin/sales/${sale.id}/inquiries`}>
              <Icon name="inbox" /><span>Inquiries</span>
              {sale.newInquiryCount > 0 && <span className="badge">{sale.newInquiryCount}</span>}
            </Link>
          </nav>
          {sale.status === 'DRAFT' && (
            <p className="notice small"><Icon name="info" /> Draft: only you and its sellers can see this page. Publish it below when it's ready.</p>
          )}
        </>
      )}

      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>Sale name</span>
          <input
            value={form.title}
            maxLength={TITLE_MAX}
            placeholder="e.g. Cox family moving sale"
            onChange={(e) => set({ title: e.target.value, ...(slugTouched.current ? {} : { slug: slugify(e.target.value) }) })}
          />
          {fields.title && <small className="error">{fields.title}</small>}
        </label>

        <label className="field">
          <span>Link</span>
          <div className="slug-input">
            <span className="muted">{origin.replace(/^https?:\/\//, '')}/</span>
            <input
              value={form.slug}
              maxLength={40}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => {
                slugTouched.current = true;
                set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') });
              }}
            />
          </div>
          {fields.slug ? (
            <small className="error">{fields.slug}</small>
          ) : slugCheck?.slug === form.slug ? (
            <small className={slugCheck.available ? 'ok' : 'error'}>{slugCheck.available ? 'Available' : slugCheck.reason}</small>
          ) : (
            <small className="muted">Letters, numbers and dashes. This is what you share.</small>
          )}
          {slugChangedWhileLive && <small className="warn">The old link /{sale!.slug} will stop working.</small>}
        </label>

        <label className="field">
          <span>General area</span>
          <input value={form.publicLocation} maxLength={LOCATION_MAX} placeholder="e.g. Heights, Houston" onChange={(e) => set({ publicLocation: e.target.value })} />
          <small className="muted">A neighborhood or city, never the street address.</small>
          {fields.publicLocation && <small className="error">{fields.publicLocation}</small>}
        </label>

        <fieldset className="field members">
          <legend>Sellers who manage this sale</legend>
          <MembersEditor members={members} sellers={sellers} onChange={(m) => { setSaved(false); setMembers(m); }} />
          {noOwner && <small className="warn">Add at least one owner: owners change settings and get inquiry emails.</small>}
          {fields.members && <small className="error">{fields.members}</small>}
        </fieldset>

        <details className="more" open={Boolean(sale)}>
          <summary>More details (optional)</summary>
          <div className="stack">
            <label className="field">
              <span>How to buy</span>
              <textarea rows={3} maxLength={CONTACT_NOTE_MAX} value={form.contactNote} placeholder="e.g. Text 555-0100 with the item #. Cash or Venmo. Pickup only." onChange={(e) => set({ contactNote: e.target.value })} />
            </label>
            <label className="field">
              <span>About the sale</span>
              <textarea rows={3} maxLength={SALE_DESCRIPTION_MAX} value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </label>
            <div className="two-col">
              <label className="field">
                <span>Last day</span>
                <input type="date" value={form.endsOn} onChange={(e) => set({ endsOn: e.target.value })} />
                {fields.endsOn && <small className="error">{fields.endsOn}</small>}
              </label>
              <label className="field">
                <span>Timezone</span>
                <select value={form.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                  {!TIMEZONES.some(([tz]) => tz === form.timezone) && <option value={form.timezone}>{form.timezone}</option>}
                  {TIMEZONES.map(([tz, label]) => <option key={tz} value={tz}>{label}</option>)}
                </select>
              </label>
            </div>
            <label className="field">
              <span>Street address (private)</span>
              <input value={form.privateAddress} maxLength={PRIVATE_ADDRESS_MAX} onChange={(e) => set({ privateAddress: e.target.value })} />
              <small className="muted">Never shown publicly. For your records only.</small>
            </label>
          </div>
        </details>

        <fieldset className="status-picker">
          <legend>Status</legend>
          {SALE_STATUSES.map((s) => (
            <label key={s} className={form.status === s ? 'on' : ''}>
              <input type="radio" name="status" value={s} checked={form.status === s} onChange={() => set({ status: s })} />
              <strong>{SALE_STATUS_LABEL[s]}</strong>
              <span className="muted small">{SALE_STATUS_HELP[s]}</span>
            </label>
          ))}
        </fieldset>

        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn primary big" disabled={busy}>
          {busy ? 'Saving…' : !sale ? 'Create sale' : saved ? 'Saved' : 'Save changes'}
        </button>
      </form>
    </main>
  );
}

function MembersEditor({ members, sellers, onChange }: { members: SaleMemberInput[]; sellers: Seller[]; onChange: (m: SaleMemberInput[]) => void }) {
  const [adding, setAdding] = useState('');
  const byId = (id: string) => sellers.find((s) => s.id === id);
  const available = sellers.filter((s) => !members.some((m) => m.userId === s.id));

  return (
    <div className="member-list">
      {members.length === 0 && <p className="muted small">No sellers yet.</p>}
      {members.map((m) => {
        const s = byId(m.userId);
        return (
          <div key={m.userId} className="member">
            <span className="member-name">
              <strong>{s?.displayName ?? 'Unknown'}</strong>
              <span className="muted small">{s?.email}{s?.status === 'DISABLED' ? ' · disabled' : ''}</span>
            </span>
            <select
              aria-label={`Role for ${s?.displayName}`}
              value={m.role}
              onChange={(e) => onChange(members.map((x) => (x.userId === m.userId ? { ...x, role: e.target.value as SaleRole } : x)))}
            >
              <option value="OWNER">Owner</option>
              <option value="EDITOR">Editor</option>
            </select>
            <button type="button" className="icon-btn" aria-label={`Remove ${s?.displayName}`} onClick={() => onChange(members.filter((x) => x.userId !== m.userId))}>×</button>
          </div>
        );
      })}
      {available.length > 0 && (
        <div className="assign-row">
          <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Add a seller">
            <option value="">Add a seller…</option>
            {available.map((s) => <option key={s.id} value={s.id}>{s.displayName} ({s.email})</option>)}
          </select>
          <button
            type="button"
            className="btn"
            disabled={!adding}
            onClick={() => {
              onChange([...members, { userId: adding, role: members.some((m) => m.role === 'OWNER') ? 'EDITOR' : 'OWNER' }]);
              setAdding('');
            }}
          >
            Add
          </button>
        </div>
      )}
      <Link className="link small" to="/platform-admin/sellers/new">+ Create a new seller</Link>
    </div>
  );
}
